// apps/web/src/self-service/token-storage.ts — Phase 4 plan 04-04.
//
// URL helpers for the self-service token. The plan called for a
// hash-based read (`#token=raw`) + a path-based write
// (`/reservierung/raw`). The implementation we actually ship:
//
//   1. The A7 confirmation page emits a `selfServiceUrl` whose path
//      IS the token (`/reservierung/<raw>`) — the server builds the
//      URL from `env.PUBLIC_WEB_ORIGIN` + `/reservierung/` +
//      `encodeURIComponent(token)` (see
//      `apps/api/src/graphql/resolvers/reservation-public.ts:616`).
//   2. The A8 page reads the token from `useParams().token` (the
//      URL path), NOT from the hash.
//   3. The hash helpers exist as a defense-in-depth fallback: if a
//      future email client or routing layer ever delivers the
//      token via `#token=...` (OQ-7 leak surface), the helpers
//      scrub the hash via `history.replaceState` on mount.
//
// The token is NEVER written to the query string (OQ-7 / T-04-41):
// the path is acceptable because the email is the only place the
// URL appears and it lives behind a per-token rate limit
// (`x-self-service-token` SHA-256-digested key — see
// `apps/api/src/plugins/rate-limit.ts` instance 3). The query
// string, by contrast, leaks into server logs and the Referer
// header, which is why we strip it on every transition.

export function readTokenFromHash(): string | null {
  if (typeof window === "undefined") return null;
  const hash = window.location.hash;
  if (!hash || hash.length < 2) return null;
  // Accept "#token=raw" (the documented form) — anything else is
  // undefined behaviour and we return null.
  const params = new URLSearchParams(
    hash.startsWith("#") ? hash.slice(1) : hash,
  );
  const raw = params.get("token");
  return typeof raw === "string" && raw.length > 0 ? raw : null;
}

/**
 * Write the token into the URL path (NOT the query string). Used
 * by the A7 confirmation page's "Reservierung verwalten" link —
 * the link element carries the full URL, but if a routing code
 * path ever wants to navigate imperatively, this helper is the
 * canonical write site. Returns the new path-only URL.
 */
export function writeTokenToUrl(token: string): string {
  if (typeof window === "undefined")
    return `/reservierung/${encodeURIComponent(token)}`;
  const target = `/reservierung/${encodeURIComponent(token)}`;
  window.history.replaceState(null, "", target);
  return target;
}

/**
 * Strip the token from the URL bar. Called when the guest cancels
 * the reservation (A11) and clicks "Neue Reservierung" — the next
 * booking flow must not leak the previous reservation's token via
 * the address bar. Replaces the current history entry so the
 * back-button doesn't replay the token either.
 */
export function clearTokenFromUrl(): void {
  if (typeof window === "undefined") return;
  window.history.replaceState(null, "", "/reservierung/bestaetigung");
}

/**
 * Strip any query/hash params on the URL bar (defense in depth —
 * even if the A7 navigation code leaked params, the A8 page
 * scrubs them on mount).
 */
export function scrubQueryAndHash(): void {
  if (typeof window === "undefined") return;
  if (window.location.search.length > 0 || window.location.hash.length > 0) {
    window.history.replaceState(null, "", window.location.pathname);
  }
}
